import type { MembershipDetails } from '../../entities/entityTypes';
import { useRefetchMembershipDetailsMutationOptions } from '../../entities/membership/hooks/useRefetchMembershipDetailsMutationOptions';
import { requestBuilder } from '../../http/RequestBuilder';

/**
 * Body for PATCH `/api/admin/memberships/:id/members/:userId/member-journey-log`.
 * Matches backend `AdminUpdateMemberJourneyLogDto`: each field is an ISO datetime
 * string or `null` to clear; omit keys you are not changing.
 */
export type AdminUpdateMemberJourneyLogBody = Partial<{
    completedGatedOnboarding: string | null;
    viewedLinkMoreAccounts: string | null;
    bookedDiscoverySession: string | null;
    completedDiscoverySession: string | null;
    bookedRoadmapSession: string | null;
    completedRoadmapSession: string | null;
    completedMoneyMap: string | null;
    moneyMapFinalizedForAction: string | null;
    /** Deprecated - feature no longer exists */
    completedWelcomeVideo: string | null;
}>;

const useUpdateMemberJourneyLog = requestBuilder.mutation<
    unknown,
    AdminUpdateMemberJourneyLogBody,
    { id: string; userId: string }
>({
    endpoint: ({ id, userId }) =>
        `/api/admin/memberships/${id}/members/${userId}/member-journey-log`,
    method: 'PATCH',
});

export const useUpdateMemberJourneyLogAndRefetch = (
    membership: MembershipDetails | null,
) => {
    const refetchMembershipMutationOptions =
        useRefetchMembershipDetailsMutationOptions(membership);
    const { mutateAsync, isPending } = useUpdateMemberJourneyLog(
        refetchMembershipMutationOptions,
    );

    const updateMemberJourneyLogAsync = async ({
        userId,
        body,
    }: {
        userId: string;
        body: AdminUpdateMemberJourneyLogBody;
    }) => {
        if (membership == null) {
            return;
        }
        await mutateAsync({
            urlParams: { id: membership.id, userId },
            body,
        });
    };

    return {
        updateMemberJourneyLogAsync,
        isPending,
    };
};
